import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Ban,
  Copy,
  Download,
  FilePenLine,
  FileSignature,
  Link2,
  MoreHorizontal,
  Plus,
  Trash2,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import ContextMenu from '../../components/ui/ContextMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import ModuleIcon from '../../components/public/ModuleIcon';
import { getErrorMessage } from '../../lib/apiClient';
import { formatDateNumeric } from '../../utils/format';
import {
  cloneDocument,
  deleteDocument,
  downloadDocument,
  listDocuments,
  signatureKeys,
  voidDocument,
} from './signaturesApi';
import StatusBadge from './components/StatusBadge';

/** Dashboard — every signature document in the workspace, with signer progress. */
export default function SignaturesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [newDocOpen, setNewDocOpen] = useState(false);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: signatureKeys.list(),
    queryFn: () => listDocuments({ limit: 50 }),
  });
  const documents = data?.data ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['signatures'] });

  const clone = useMutation({
    mutationFn: cloneDocument,
    onSuccess: (doc) => {
      toast.success('Draft copy created');
      refresh();
      navigate(`/signatures/${doc.id}/edit?step=1`);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const voidDoc = useMutation({
    mutationFn: voidDocument,
    onSuccess: () => {
      toast.success('Document voided');
      refresh();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      toast.success('Document deleted');
      setDeleteTarget(null);
      refresh();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const menuItems = (doc) => [
    doc.status === 'draft' && {
      icon: FilePenLine,
      label: 'Continue editing',
      onClick: () => navigate(`/signatures/${doc.id}/edit?step=${doc.step ?? 1}`),
    },
    doc.status !== 'draft' && {
      icon: Download,
      label: 'Download PDF',
      onClick: () =>
        downloadDocument(doc.id, doc.title).catch((err) => toast.error(getErrorMessage(err))),
    },
    doc.type === 'waiver' &&
      doc.publicSlug &&
      doc.status === 'published' && {
        icon: Link2,
        label: 'Copy public link',
        onClick: async () => {
          await navigator.clipboard.writeText(`${window.location.origin}/w/${doc.publicSlug}`);
          toast.success('Link copied');
        },
      },
    { separator: true },
    {
      icon: Copy,
      label: 'Clone as draft',
      onClick: () => clone.mutate(doc.id),
    },
    (doc.status === 'published' || doc.status === 'draft') && {
      icon: Ban,
      label: 'Void',
      danger: true,
      onClick: () => voidDoc.mutate(doc.id),
    },
    {
      icon: Trash2,
      label: 'Delete',
      danger: true,
      onClick: () => setDeleteTarget(doc),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
            <ModuleIcon slug="sign" decorative className="h-7 w-7" />
            Signatures
          </h1>
          <p className="text-sm text-slate-500">
            Send PDFs for signature or share public document links.
          </p>
        </div>
        <Button onClick={() => setNewDocOpen(true)}>
          <Plus className="h-4 w-4" /> New document
        </Button>
      </div>

      {documents.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-100 py-16 text-center">
          <FileSignature className="h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">No signature documents yet.</p>
          <Button variant="secondary" size="sm" onClick={() => setNewDocOpen(true)}>
            Send your first document
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3 font-semibold">Document</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Signed</th>
                <th className="px-4 py-3 font-semibold">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-slate-200/50">
                  <td className="px-4 py-3">
                    <Link
                      to={
                        doc.status === 'draft'
                          ? `/signatures/${doc.id}/edit?step=${doc.step ?? 1}`
                          : `/signatures/${doc.id}`
                      }
                      className="font-medium text-slate-800 hover:text-brand-600"
                    >
                      {doc.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 capitalize text-slate-500">
                    {doc.type === 'waiver' ? 'Document' : doc.type}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={doc.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {doc.type === 'waiver'
                      ? `${doc.signerCount} submission${doc.signerCount === 1 ? '' : 's'}`
                      : `${doc.signedCount}/${doc.signerCount}`}
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {formatDateNumeric(doc.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      aria-label="Actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setCtxMenu(
                          ctxMenu?.docId === doc.id
                            ? null
                            : {
                                docId: doc.id,
                                x: rect.right,
                                y: rect.bottom + 6,
                                items: menuItems(doc),
                              },
                        );
                      }}
                      className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New document: choose the flow (OtterWaiver's Direct vs Bulk PDF modal) */}
      <Modal open={newDocOpen} onClose={() => setNewDocOpen(false)} title="New signature document">
        <div className="space-y-3">
          <FlowChoice
            title="Agreement"
            description="Send one PDF to up to 5 named recipients. Everyone signs the same document and receives the final copy."
            onClick={() => navigate('/signatures/new?type=agreement')}
          />
          <FlowChoice
            title="Document (public link)"
            description="Share one link with anyone. Each person signs and gets their own copy — ideal for consent forms and other public documents."
            onClick={() => navigate('/signatures/new?type=waiver')}
          />
        </div>
      </Modal>
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => remove.mutate(deleteTarget.id)}
        loading={remove.isPending}
        title="Delete document?"
        description={deleteTarget ? `“${deleteTarget.title}” will be permanently deleted.` : ''}
        confirmLabel="Delete document"
      />
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={ctxMenu.items}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}

function FlowChoice({ title, description, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-left transition-colors hover:border-brand-500 hover:bg-brand-600/5"
    >
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">{description}</p>
    </button>
  );
}
